import { Button, Flex, Modal, Tabs } from "antd";
import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AiOutlineArrowRight,
  AiOutlineCheck,
  AiOutlineClose,
} from "react-icons/ai";

import { useThemeMode } from "sandbox/contexts/ThemeModeContext";

import s from "./SandboxHelpModal.module.css";

interface SandboxHelpModalProps {
  onClose: () => void;
  open: boolean;
}

type HelpTabKey =
  | "whatIsSandbox"
  | "generateCredentials"
  | "apiExplorer"
  | "postman";

interface HelpSlide {
  content: ReactNode;
  image: string;
}

const BulletList = ({
  items,
  isDark,
}: {
  isDark: boolean;
  items: string[];
}) => (
  <Flex component="ul" vertical gap="small" className={s.bulletList}>
    {items.map(item => (
      <Flex
        component="li"
        key={item}
        align="center"
        gap="small"
        className={s.bulletItem}
      >
        <span
          className={clsx(s.bulletIconWrap, isDark && s.bulletIconWrapDark)}
        >
          <AiOutlineCheck
            className={clsx(s.bulletIcon, isDark && s.bulletIconDark)}
          />
        </span>
        <span>{item}</span>
      </Flex>
    ))}
  </Flex>
);

export const SandboxHelpModal = ({ open, onClose }: SandboxHelpModalProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { mode } = useThemeMode();
  const [activeTab, setActiveTab] = useState<HelpTabKey>("whatIsSandbox");
  const [slideIndex, setSlideIndex] = useState(0);

  const TABS: { key: HelpTabKey; label: string }[] = [
    {
      key: "whatIsSandbox",
      label: t("sandbox:components.sandboxHelpModal.tabs.whatIsSandbox"),
    },
    {
      key: "generateCredentials",
      label: t("sandbox:components.sandboxHelpModal.tabs.generateCredentials"),
    },
    {
      key: "apiExplorer",
      label: t("sandbox:components.sandboxSidebar.labels.sandbox"),
    },
    {
      key: "postman",
      label: t("sandbox:components.sandboxHelpModal.tabs.postman"),
    },
  ];

  const getSlidesForTab = (tab: HelpTabKey): HelpSlide[] => {
    switch (tab) {
      case "whatIsSandbox":
        return [
          {
            image: "/images/welcomeToSandbox.png",
            content: (
              <>
                <h3 className={s.slideTitle}>
                  {t("sandbox:auth.title.signIn")}
                </h3>
                <p className={s.slideDescription}>
                  {t("sandbox:components.sandboxHelpModal.whatIsSandbox.intro")}
                </p>
              </>
            ),
          },
        ];
      case "generateCredentials":
        return [
          {
            image: "/images/generateCredentialsSandbox.png",
            content: (
              <>
                <h3 className={s.slideTitle}>
                  {t(
                    "sandbox:components.sandboxHelpModal.tabs.generateCredentials",
                  )}
                </h3>
                <p className={s.slideDescription}>
                  {t(
                    "sandbox:components.sandboxHelpModal.generateCredentials.description",
                  )}
                </p>
                <p className={s.slideDescription}>
                  {t(
                    "sandbox:components.sandboxHelpModal.generateCredentials.instructions",
                  )}
                </p>
              </>
            ),
          },
        ];
      case "apiExplorer":
        return [
          {
            image: "/images/usingApiExplorerSandbox.png",
            content: (
              <>
                <h3 className={s.slideTitle}>
                  {t("sandbox:components.sandboxHelpModal.apiExplorer.title")}
                </h3>
                <p className={s.slideDescription}>
                  {t(
                    "sandbox:components.sandboxHelpModal.apiExplorer.description",
                  )}
                </p>
                <p className={s.slideSubheading}>
                  {t("sandbox:components.sandboxHelpModal.perfectFor")}
                </p>
                <BulletList
                  isDark={mode === "dark"}
                  items={[
                    t(
                      "sandbox:components.sandboxHelpModal.apiExplorer.bullets.quickTesting",
                    ),
                    t(
                      "sandbox:components.sandboxHelpModal.apiExplorer.bullets.exploringBehaviour",
                    ),
                    t(
                      "sandbox:components.sandboxHelpModal.apiExplorer.bullets.learningFormats",
                    ),
                  ]}
                />
              </>
            ),
          },
        ];
      case "postman":
        return [
          {
            image: "/images/usingPostmanSandbox.png",
            content: (
              <>
                <h3 className={s.slideTitle}>
                  {t("sandbox:components.sandboxHelpModal.postman.title")}
                </h3>
                <p className={s.slideDescription}>
                  {t("sandbox:components.sandboxHelpModal.postman.description")}
                </p>
                <p className={s.slideSubheading}>
                  {t("sandbox:components.sandboxHelpModal.perfectFor")}
                </p>
                <BulletList
                  isDark={mode === "dark"}
                  items={[
                    t(
                      "sandbox:components.sandboxHelpModal.postman.bullets.buildingPrototypes",
                    ),
                    t(
                      "sandbox:components.sandboxHelpModal.postman.bullets.savingRequests",
                    ),
                    t(
                      "sandbox:components.sandboxHelpModal.postman.bullets.testingWorkflows",
                    ),
                  ]}
                />
              </>
            ),
          },
        ];
    }
  };

  const tabIndex = TABS.findIndex(tab => tab.key === activeTab);
  const slides = getSlidesForTab(activeTab);
  const slide = slides[slideIndex];
  const isLastTab = tabIndex === TABS.length - 1;

  const handleTabChange = (key: string) => {
    setActiveTab(key as HelpTabKey);
    setSlideIndex(0);
  };

  const handleNext = () => {
    if (isLastTab) {
      onClose();
      return;
    }
    setActiveTab(TABS[tabIndex + 1].key);
    setSlideIndex(0);
  };

  const getNextStep = () => {
    if (isLastTab) {
      return { label: t("sandbox:actions.exploreSandbox"), primary: true };
    }
    return { label: t("common:components.buttons.next"), primary: false };
  };

  const { label: nextLabel, primary: isNextPrimary } = getNextStep();

  const tabs = TABS.map(({ key, label }) => ({
    key,
    label: (
      <span
        className={clsx(
          key === activeTab && s.activeTabLabel,
          key === activeTab && mode === "dark" && s.activeTabLabelDark,
        )}
      >
        {label}
      </span>
    ),
    children: null,
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={840}
      centered
      styles={{ container: { padding: 0 }, body: { padding: 0 } }}
      destroyOnHidden
    >
      <Flex vertical className={s.modalBody}>
        <Flex align="center" justify="space-between" className={s.header}>
          <span className={s.title}>
            {t("sandbox:components.sandboxHeader.sandboxHelp")}
          </span>
          <Button
            type="text"
            icon={<AiOutlineClose />}
            onClick={onClose}
            aria-label={t("common:components.buttons.close")}
            className={s.closeButton}
          />
        </Flex>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabs}
          classNames={{ header: s.tabsNav, body: s.tabsBody }}
        />
        <Flex align="stretch" className={s.row}>
          <div className={s.imageWrap}>
            <img src={slide.image} alt="" className={s.image} />
          </div>
          <Flex vertical justify="space-between" className={s.textWrap}>
            <Flex vertical gap="small" className={s.textContent}>
              {slide.content}
            </Flex>
            <Flex justify="end" className={s.slideNav}>
              <Button
                type={isNextPrimary ? "primary" : "default"}
                icon={<AiOutlineArrowRight />}
                iconPlacement="end"
                onClick={handleNext}
              >
                {nextLabel}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
